import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import './RatingSlider.css';

interface RatingSliderProps {
  onRate: (rating: number) => void;
  showHint: boolean;
  disabled: boolean;
}

const RatingSlider: React.FC<RatingSliderProps> = ({ onRate, showHint, disabled }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [currentRating, setCurrentRating] = useState(5);
  const sliderRef = useRef<HTMLDivElement>(null);

  const handleInteractionStart = useCallback(() => {
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleInteractionEnd = useCallback(() => {
    if (isDragging && !disabled) {
      onRate(currentRating);
      setIsDragging(false);
      setCurrentRating(5);
    }
  }, [isDragging, disabled, onRate, currentRating]);

  const handleInteractionMove = useCallback((clientY: number) => {
    if (!isDragging || !sliderRef.current || disabled) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const y = clientY - rect.top;
    const percentage = Math.max(0, Math.min(1, 1 - y / rect.height));
    const rating = Math.round(percentage * 10);

    setCurrentRating(rating);
  }, [isDragging, disabled]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      handleInteractionMove(e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleInteractionMove(e.touches[0].clientY);
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('mouseup', handleInteractionEnd);
      window.addEventListener('touchend', handleInteractionEnd);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('mouseup', handleInteractionEnd);
        window.removeEventListener('touchend', handleInteractionEnd);
      };
    }
    return undefined;
  }, [isDragging, handleInteractionMove, handleInteractionEnd]);

  return (
    <div className="rating-slider-container">
      {showHint && !isDragging && (
        <motion.div
          className="rating-hint"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
        >
          ↓ Scroll to rate
        </motion.div>
      )}

      <div
        ref={sliderRef}
        className={`rating-slider ${isDragging ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
        onMouseDown={handleInteractionStart}
        onTouchStart={handleInteractionStart}
      >
        <div className="rating-track">
          {[...Array(11)].map((_, i) => (
            <div key={i} className="rating-mark">
              <span className="rating-label">{i}</span>
            </div>
          ))}
        </div>

        {isDragging && (
          <motion.div
            className="rating-indicator"
            style={{
              bottom: `${(currentRating / 10) * 100}%`,
            }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
          >
            <div className="rating-value">{currentRating}</div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RatingSlider;
